import Select, {
  components,
  MultiValue,
  MultiValueProps,
  SingleValue,
} from 'react-select';
import ProdutoModel from 'models/Produto';
import { useState } from 'react';

export type Option = {
  label: string;
  value: number;
};

type BaseProps = {
  produtos: ProdutoModel[];
};

type SingleProps = BaseProps & {
  isMulti?: false;
  value: Option | null;
  onChange: (value: Option | null) => void;
};

type MultiProps = BaseProps & {
  isMulti: true;
  value: Option[];
  onChange: (value: Option[]) => void;
};

type SelectProdutosProps = SingleProps | MultiProps;

/**
 *
 Seleção de um campo
  <SelectProdutos
    produtos={produtos}
    onChange={setProdutoFiltrar}
    value={produtoFiltrar}
  />


 Seleção de multiplos campos
  <SelectProdutos
    produtos={produtosSelecao}
    onChange={setProdutosAssociados}
    value={Object.values(produtosAssociados)}
    isMulti
  />
 */
export default function SelectProdutos(
  props: SelectProdutosProps,
): JSX.Element {
  const { produtos, value, isMulti } = props;
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const options: Option[] = produtos.map(item => ({
    label: `${item.nome} (${item.ProdutoCategoria?.descricao})`,
    value: item.id,
  }));

  const MultiValueCustom = (props2: MultiValueProps<Option>) => {
    const values = props2.selectProps.value as Option[];

    // Quando o menu está aberto, mostra todos os produtos
    if (menuIsOpen) {
      return <components.MultiValue {...props2} />;
    }

    // Quando está fechado, mostra somente o primeiro
    // e a quantidade restante
    const index = values.findIndex(item => item.value === props2.data.value);

    if (index === 0) {
      return (
        <components.MultiValue {...props2}>
          {props2.data.label}
          {values.length > 1 && ` + ${values.length - 1}`}
        </components.MultiValue>
      );
    }

    return null;
  };

  function criarOpcoes(option: Option, produtos2: ProdutoModel[]): Option {
    const produto = produtos2.find(p => p.id === option.value);

    return {
      value: option.value,
      label: `${produto?.nome} (${produto?.ProdutoCategoria?.descricao})`,
    };
  }

  let valueComDescricao: Option | Option[] | null = null;

  if (value) {
    if (isMulti) {
      valueComDescricao = (value as Option[]).map(v =>
        criarOpcoes(v, produtos),
      );
    } else {
      valueComDescricao = criarOpcoes(value as Option, produtos);
    }
  }

  const handleChange = (selected: MultiValue<Option> | SingleValue<Option>) => {
    if (props.isMulti) {
      props.onChange([...(selected as MultiValue<Option>)]);
    } else {
      props.onChange(selected as SingleValue<Option>);
    }
  };

  return (
    <Select<Option, boolean>
      placeholder="Selecione..."
      options={options}
      value={valueComDescricao}
      isMulti={isMulti}
      closeMenuOnSelect={!isMulti}
      onChange={handleChange}
      menuIsOpen={undefined}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={() => setMenuIsOpen(false)}
      components={{
        MultiValue: MultiValueCustom,
      }}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{
        control: (base: any) => ({
          ...base,
          minHeight: 38,
        }),

        valueContainer: (base: any) => ({
          ...base,
          maxHeight: menuIsOpen ? undefined : 36,
          overflow: menuIsOpen ? 'visible' : 'hidden',
        }),

        menuPortal: (base: any) => ({
          ...base,
          zIndex: 9999,
        }),
      }}
    />
  );
}
